import config from "aldoo-ra/Config/index.jsx"
import { IMAGE_VARIANT_WIDTHS, srcsetFor } from "aldoo-ra/ImageVariants"
const { CMS_STATIC_URL, PLATFORM_API_URL } = config
const STATIC_URL = CMS_STATIC_URL
  ? CMS_STATIC_URL
  : PLATFORM_API_URL + "/cms/static"

//Return the full CMS URL for a given relative path
export const url = (path) => {
  if (!path) return ""

  if (Array.isArray(path) && path.length > 0) {
    path = path[0]
  }

  //if the path is already a full URL, return it
  if (path.startsWith("http") || path.startsWith("blob")) return path
  //otherwise, prepend the base URL
  return STATIC_URL + "/" + path
}

// Variant widths served by the api's ?size= resizer (aldoo-na/image-variants,
// mounted on /cms/static among others) — kept in aldoo-ra/ImageVariants
export { IMAGE_VARIANT_WIDTHS }

//Return the CMS URL for a path, resized server-side to the given width
export const sizedUrl = (path, width) => {
  const src = url(path)
  if (!src || !src.startsWith(STATIC_URL)) return src
  return `${src}?size=${width}`
}

/**
 * Responsive <img> props for a CMS image: src + srcSet over the variant
 * width buckets + sizes. The browser then requests the smallest variant
 * covering the displayed width. Non-CMS URLs and SVGs return src only.
 * @param {string|string[]} path - CMS image path or URL
 * @param {string} sizes - the img sizes attribute, e.g. "(min-width:1080px) 450px, 90vw"
 */
export const imageProps = (path, sizes = "100vw") => {
  const src = url(path)
  if (!src || !src.startsWith(STATIC_URL) || src.endsWith(".svg")) return { src }
  return {
    src,
    srcSet: srcsetFor(src),
    sizes,
  }
}

//Return the full API URL for a given relative path
//Used for API-specific resources like screenshots
export const apiUrl = (path) => {
  if (!path) return ""

  if (Array.isArray(path) && path.length > 0) {
    path = path[0]
  }

  //if the path is already a full URL, return it
  if (path.startsWith("http") || path.startsWith("blob")) return path
  //otherwise, prepend the API URL
  return PLATFORM_API_URL + path
}

//Return the filename from a path
//e.g. filename('path/to/file.jpg') returns 'file.jpg'
export const filename = (path) => {
  if (!path) return ""
  //if this is youtube or vimeo, return the full URL
  if (path.includes("youtube") || path.includes("youtu.be")) return path

  return path.split("/").pop()
}

/**
 * Return the relative path from a full URL by removing the static URL base
 * This is the inverse of the url() function
 * @param {string} fullUrl - The full URL to convert to relative path
 * @returns {string} - The relative path
 */
export const getRelativePath = (fullUrl) => {
  if (!fullUrl) return ""

  // If it's already a relative path, return it
  if (!fullUrl.startsWith("http") && !fullUrl.startsWith("blob")) return fullUrl

  // Remove the static URL base to get the relative path
  if (fullUrl.startsWith(STATIC_URL + "/")) {
    return fullUrl.substring((STATIC_URL + "/").length)
  }

  // If it doesn't match our static URL, return the original
  return fullUrl
}

/**
 * Recursively transforms media URLs in objects and arrays
 * @param {any} data - The data to process
 * @param {function} transformFunction - URL transformation function
 * @returns {any} - Transformed data
 */
export const transformUrls = (data, transformFunction) => {
  // Handle null or undefined
  if (data == null) return data

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map((item) => transformUrls(item, transformFunction))
  }

  // Handle objects
  if (typeof data === "object") {
    return Object.entries(data).reduce((acc, [key, value]) => {
      acc[key] = transformUrls(value, transformFunction)
      return acc
    }, {})
  }

  // Handle strings - check for media paths
  if (typeof data === "string") {
    const mediaPatterns = ["files/", "images/", "videos/", "audio/", "user-avatars/"]
    if (mediaPatterns.some((pattern) => data.startsWith(pattern))) {
      return transformFunction(data)
    }
  }

  // Return unchanged value for other types
  return data
}

/**
 * Transforms media URLs in a content object.
 * This will go through all fields and check if they are media URLs.
 * If so, it will transform then using the url() function converting them to full URLs.
 * @param {*} content
 * @returns
 */
export const absoluteURLFields = (content) => {
  return transformUrls(content, url)
}
